/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type {
  CalendarInput,
  CalendarUpdateInput,
} from '@services/requests/housien-models/CalendarInput';
import type { UserRole } from '@/shared/utils/permissions';
import { FF_APP_GOOGLE_CALENDAR_INTEGRATION } from '@/shared/utils/feature-flag';
import { canManageCalendar } from '@features/agenda/utils/agenda-permissions';
import { isPrimaryCalendar } from '@features/agenda/utils/calendar-display';
import {
  googleConnectionState,
  importedGoogleCalendars,
  ownGoogleCalendar,
} from '@features/agenda/utils/google-connection';
import { AdaptiveFormShell } from '@/shared/ui/components/form/AdaptiveFormShell';
import { CalendarSettingsNav } from './CalendarSettingsDrawer.nav';
import type { SettingsTab } from './CalendarSettingsDrawer.nav';
import { AgendasPane } from './CalendarSettingsDrawer.agendas';
import { CalendarSettingsGoogleSection } from './CalendarSettingsGoogleSection';
import {
  useCalendarDraft,
  NEW_CALENDAR,
} from './CalendarSettingsDrawer.useDraft';

export interface CalendarSettingsDrawerProps {
  open: boolean;
  agencyId: string;
  currentUserEmail: string;
  userRole: UserRole;
  calendars: Calendar[];
  agencyName?: string;
  loading?: boolean;
  isSaving?: boolean;
  onCreate: (input: CalendarInput) => void;
  onUpdate: (calendarId: string, input: CalendarUpdateInput) => void;
  onDelete: (calendarId: string) => void;
  onClose: () => void;
}

/**
 * Resolves the browser's IANA time zone, falling back to `UTC`.
 * @returns The resolved time zone identifier.
 */
const tz = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/**
 * Splits the calendars into the three groups the settings present.
 *
 * @param calendars - Calendars visible to the user.
 * @param userEmail - Email of the signed-in user.
 * @param userRole - Role deciding what the user may manage.
 * @returns The institutional calendar, the user's own, and the imported ones.
 */
const groupCalendars = (
  calendars: Calendar[],
  userEmail: string,
  userRole: UserRole,
) => {
  const google = importedGoogleCalendars(calendars, userEmail);
  const googleIds = new Set(google.map((c) => c.calendar_id));
  return {
    institutional: calendars.find(isPrimaryCalendar),
    google,
    personal: calendars.filter(
      (c) =>
        !isPrimaryCalendar(c) &&
        !googleIds.has(c.calendar_id) &&
        canManageCalendar(userRole, c),
    ),
  };
};

/**
 * Calendar settings: the calendars themselves, and the Google link.
 *
 * Two sections rather than one long scroll, because they answer different
 * questions — what calendars exist, and what Housien and Google exchange —
 * and the second one grew past the point where it could sit as a footer
 * under the first.
 *
 * @param props - Calendars, permissions and the write handlers.
 * @returns The rendered settings.
 */
export const CalendarSettingsDrawer: React.FC<CalendarSettingsDrawerProps> = ({
  open,
  agencyId,
  currentUserEmail,
  userRole,
  calendars,
  agencyName,
  loading = false,
  isSaving = false,
  onCreate,
  onUpdate,
  onDelete,
  onClose,
}) => {
  const { t } = useTranslation('features/agenda');
  const showGoogle = FF_APP_GOOGLE_CALENDAR_INTEGRATION;
  const [tab, setTab] = useState<SettingsTab>('calendars');
  const draft = useCalendarDraft(open);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) setTab('calendars');
  }, [open]);

  useEffect(() => {
    if (saving && !isSaving) {
      setSaving(false);
      draft.finish();
    }
  }, [saving, isSaving, draft]);

  const groups = useMemo(
    () => groupCalendars(calendars, currentUserEmail, userRole),
    [calendars, currentUserEmail, userRole],
  );
  const googleState = googleConnectionState(
    ownGoogleCalendar(calendars, currentUserEmail),
  );

  const submitDraft = () => {
    const name = draft.name.trim();
    if (!name) return;
    setSaving(true);
    if (draft.editingId === NEW_CALENDAR) {
      onCreate({
        name,
        owner_type: 'user',
        owner_id: currentUserEmail,
        color: draft.color,
        default_timezone: tz(),
      });
      return;
    }
    if (draft.editingId) {
      onUpdate(draft.editingId, { name, color: draft.color });
    }
  };

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={t('calendar_settings.title')}
      density="settings"
    >
      <div className="flex h-full min-h-0 flex-col md:flex-row">
        {showGoogle && (
          <CalendarSettingsNav
            active={tab}
            calendarCount={calendars.length}
            googleState={googleState}
            t={t}
            onChange={setTab}
          />
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-(--space-5) py-(--space-5)">
          {!showGoogle || tab === 'calendars' ? (
            <AgendasPane
              calendars={calendars}
              institutional={groups.institutional}
              personal={groups.personal}
              googleCalendars={groups.google}
              showGoogle={showGoogle}
              googleConnected={googleState !== 'disconnected'}
              googleWritable={googleState === 'connected'}
              agencyName={agencyName}
              loading={loading}
              editingId={draft.editingId}
              draftName={draft.name}
              draftColor={draft.color}
              isSaving={isSaving}
              t={t}
              onStartCreate={draft.startCreate}
              onStartEdit={draft.startEdit}
              onDraftNameChange={draft.setName}
              onDraftColorChange={draft.setColor}
              onSubmitDraft={submitDraft}
              onCancelDraft={draft.cancel}
              onDelete={onDelete}
              onGoToGoogle={() => setTab('google')}
            />
          ) : (
            <CalendarSettingsGoogleSection
              agencyId={agencyId}
              currentUserEmail={currentUserEmail}
              calendars={calendars}
            />
          )}
        </div>
      </div>
    </AdaptiveFormShell>
  );
};
